import React, { useCallback, useRef } from 'react';
import {
  Typography,
  Stack,
  Divider,
  Box,
  MenuList,
  MenuItem,
  ListItemIcon,
  CircularProgress,
  Tooltip,
  Button,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ChatBubbleOutlineOutlinedIcon from '@mui/icons-material/ChatBubbleOutlineOutlined';
import { createChat } from '../../../actions/chat';
import { useApi } from '../../../providers/ApiContext';
import { useUser } from '../../../providers/UserContext';
import type { Chat, ChatType } from '@/types/apiTypes';

interface ChatSelectionProps {
  type: ChatType;
  new_name: string;
  chats: Chat[];
  activeChat: Chat | null;
  setActiveChat: (chat: Chat) => void;
  setChats: React.Dispatch<React.SetStateAction<Chat[]>>;
  onLoadMore: () => void;
  hasMore: boolean;
  loading: boolean;
}

function ChatSelection({
  type,
  new_name,
  chats,
  activeChat,
  setActiveChat,
  setChats,
  onLoadMore,
  hasMore,
  loading,
}: ChatSelectionProps) {
  const api = useApi();
  const user = useUser();
  const observer = useRef<IntersectionObserver | null>(null);
  const lastChatElementRef = useCallback(
    (node: HTMLElement | null) => {
      if (loading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          onLoadMore();
        }
      });
      if (node) observer.current.observe(node);
    },
    [loading, hasMore, onLoadMore],
  );

  const handleCreateChat = async () => {
    if (!api) return;
    const chat = await createChat(api, {
      name: new_name,
      type,
    });
    setChats((prevChats) => [chat, ...prevChats]);
    setActiveChat(chat);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        height: '100%',
      }}
    >
      <Grid container spacing={1} sx={{ flexShrink: 0 }}>
        <Grid size={12}>
          <Button
            variant='contained'
            onClick={handleCreateChat}
            disabled={!user?.entity_id}
            fullWidth
            sx={{
              backgroundColor: 'action.selected',
              color: 'text.primary',
              '&:hover': {
                backgroundColor: 'action.hover',
              },
              '&:disabled': {
                backgroundColor: 'action.disabledBackground',
                color: 'action.disabled',
              },
            }}
          >
              <Stack direction='row' spacing={1}>
                <AddCircleOutlineIcon />
                <Typography variant='button' display='block'>
                  {new_name}
                </Typography>
              </Stack>
            </Button>
          </Grid>

          <Grid size={12}>
            <Divider />
          </Grid>
        </Grid>

        <Box
          sx={{
            flex: 1,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box
            sx={{
              flex: 1,
              overflow: 'auto',
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-track': {
                background: '#f1f1f1',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb': {
                background: '#c1c1c1',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb:hover': {
                background: '#a8a8a8',
              },
            }}
          >
            <MenuList>
              {chats.map((chat, index) => (
                <Tooltip
                  key={chat.entity_id}
                  title={chat.name || ''}
                  placement='right'
                  arrow
                >
                  <MenuItem
                    onClick={() => setActiveChat(chat)}
                    ref={index === chats.length - 1 ? lastChatElementRef : null}
                    sx={{
                      backgroundColor:
                        activeChat?.entity_id === chat.entity_id
                          ? 'action.selected'
                          : 'transparent',
                      borderLeft:
                        activeChat?.entity_id === chat.entity_id
                          ? '3px solid'
                          : '3px solid transparent',
                      borderLeftColor:
                        activeChat?.entity_id === chat.entity_id
                          ? 'divider'
                          : 'transparent',
                      borderRadius: '4px',
                      margin: '2px 8px',
                      '&:hover': {
                        backgroundColor:
                          activeChat?.entity_id === chat.entity_id
                            ? 'action.selected'
                            : 'action.hover',
                      },
                    }}
                  >
                    <ListItemIcon>
                      <ChatBubbleOutlineOutlinedIcon
                        sx={{
                          color:
                            activeChat?.entity_id === chat.entity_id
                              ? 'text.primary'
                              : 'text.secondary',
                        }}
                      />
                    </ListItemIcon>
                    <Typography
                      variant='inherit'
                      noWrap
                      sx={{
                        color:
                          activeChat?.entity_id === chat.entity_id
                            ? 'text.primary'
                            : 'text.secondary',
                        fontWeight:
                          activeChat?.entity_id === chat.entity_id ? 500 : 400,
                      }}
                    >
                      {chat.name}
                    </Typography>
                  </MenuItem>
                </Tooltip>
              ))}
            </MenuList>
            {loading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <CircularProgress size={24} />
              </Box>
            )}
          </Box>
        </Box>
      </Box>
  );
}

export default ChatSelection;

